import React, { useContext, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import AuthContext from '../../components/shared/AuthContext';
import PageMeta from '../../components/seo/PageMeta';
import AdaptivePageShell from '../../features/adaptive-learning/AdaptivePageShell';
import { useMasteryMap } from '../../features/adaptive-learning/useAdaptiveLearning';
import { masteryStatusLabel } from '../../features/adaptive-learning/adaptiveUtils';

function statusClass(score) {
  if (score >= 80) return 'mastery-dot--strong';
  if (score >= 55) return 'mastery-dot--moderate';
  return 'mastery-dot--weak';
}

function topicLabel(row) {
  return row.subtopic || row.topic || row.subject || 'Topic';
}

export default function MasteryPage() {
  const { apiClient, userProfile } = useContext(AuthContext);
  const { data, loading, syncing, error, reload } = useMasteryMap();
  const [selected, setSelected] = useState(null);
  const [selectedKey, setSelectedKey] = useState('');
  const [detailLoading, setDetailLoading] = useState(false);
  const [query, setQuery] = useState('');

  const items = data?.items || [];
  const tree = data?.tree || [];

  const weakest = useMemo(
    () => [...items].sort((a, b) => a.mastery - b.mastery).slice(0, 6),
    [items],
  );

  const filteredTree = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return tree;
    return tree.filter((subject) => {
      const subjMatch = subject.name?.toLowerCase().includes(q);
      const childMatch = Object.values(subject.children || {}).some((topic) => {
        if (topic.name?.toLowerCase().includes(q)) return true;
        return Object.values(topic.subtopics || {}).some((st) => st.name?.toLowerCase().includes(q));
      });
      return subjMatch || childMatch;
    });
  }, [tree, query]);

  const openTopic = async (topicKey) => {
    setSelectedKey(topicKey);
    setDetailLoading(true);
    try {
      const res = await apiClient.get('/api/v1/learning/mastery/detail', { params: { topicKey } });
      setSelected(res.data);
    } catch {
      setSelected(null);
    } finally {
      setDetailLoading(false);
    }
  };

  return (
    <>
      <PageMeta title="Knowledge map" description="Your mastery signals by topic." />
      <AdaptivePageShell userProfile={userProfile} loading={loading && !data}>
        <header className="adaptive-hero">
          <h1>Knowledge map</h1>
          <p className="adaptive-hero-lead">
            Mastery signals (0–100) from practice, revision, and reading — not certification scores.
          </p>
          <div className="adaptive-hero-actions">
            <Link to="/hub/today" className="learn-btn learn-btn--primary">
              Today&apos;s plan
            </Link>
            <button type="button" className="learn-btn learn-btn-secondary" disabled={syncing} onClick={reload}>
              {syncing ? 'Updating…' : 'Refresh signals'}
            </button>
          </div>
        </header>

        {error && <p className="adaptive-error" role="alert">{error}</p>}

        {!loading && items.length === 0 && (
          <div className="adaptive-empty">
            <p>Read questions, practice, or import a notebook to build your map.</p>
            <Link to="/hub/notebooks/new" className="learn-btn learn-btn--primary">
              Create notebook &amp; import
            </Link>
          </div>
        )}

        {weakest.length > 0 && (
          <section className="adaptive-card mastery-weakest" aria-label="Weakest topics">
            <h2>Fix my weakness</h2>
            <p className="adaptive-muted">Tap a topic for a short practice recommendation.</p>
            <ul className="mastery-weakest-list">
              {weakest.filter((w) => w.mastery < 75).map((w) => (
                <li key={w.topicKey}>
                  <button type="button" onClick={() => openTopic(w.topicKey)}>
                    {topicLabel(w)} · {w.mastery}
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}

        {items.length > 0 && (
          <div className="mastery-toolbar">
            <input
              type="search"
              className="learn-input mastery-search"
              placeholder="Filter topics…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              aria-label="Filter topics"
            />
          </div>
        )}

        <div className="mastery-layout">
          <div className="mastery-tree" aria-label="Topic hierarchy">
            {loading && !data && (
              <>
                <div className="adaptive-skeleton" />
                <div className="adaptive-skeleton" />
              </>
            )}
            {filteredTree.map((subject) => (
              <div key={subject.name} className="mastery-subject">
                <div className="mastery-subject-head">
                  <span>{subject.name}</span>
                  <span className={`mastery-score ${statusClass(subject.mastery)}`}>{subject.mastery}</span>
                </div>
                {Object.values(subject.children || {}).map((topic) => (
                  <div key={`${subject.name}-${topic.name}`} className="mastery-topic">
                    <div className="mastery-topic-head">
                      <span>{topic.name}</span>
                      <span className={`mastery-score ${statusClass(topic.mastery)}`}>{topic.mastery}</span>
                    </div>
                    <ul className="mastery-subtopics">
                      {Object.values(topic.subtopics || {}).map((st) => (
                        <li key={st.topicKey}>
                          <button
                            type="button"
                            className={`mastery-subtopic-btn${selectedKey === st.topicKey ? ' mastery-subtopic-btn--selected' : ''}`}
                            onClick={() => openTopic(st.topicKey)}
                          >
                            <span>{st.name}</span>
                            <span className={`mastery-pill ${masteryStatusLabel(st.mastery).className}`}>
                              {st.mastery}
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            ))}
            {!loading && items.length > 0 && filteredTree.length === 0 && (
              <p className="adaptive-muted">No topics match your filter.</p>
            )}
          </div>

          {(selected || detailLoading) && (
            <aside className="mastery-detail" aria-live="polite">
              <button
                type="button"
                className="mastery-detail-close"
                aria-label="Close details"
                onClick={() => { setSelected(null); setSelectedKey(''); }}
              >
                ×
              </button>
              {detailLoading && <p className="adaptive-muted">Loading…</p>}
              {selected && !detailLoading && (
                <>
                  <h2>{topicLabel(selected)}</h2>
                  <p className="mastery-detail-score">
                    Mastery signal: <strong>{selected.mastery}</strong>
                  </p>
                  <span className={`mastery-pill ${masteryStatusLabel(selected.mastery).className}`}>
                    {masteryStatusLabel(selected.mastery).text}
                  </span>
                  {selected.recommendation && (
                    <p className="adaptive-muted" style={{ marginTop: '0.75rem' }}>
                      Suggested: {selected.recommendation.questionCount} questions · ~
                      {selected.recommendation.estimatedMinutes} min
                    </p>
                  )}
                  <div className="mastery-detail-actions">
                    <Link to="/practice" className="learn-btn learn-btn--primary">
                      Improve this topic
                    </Link>
                    <Link to="/tutor" className="learn-btn learn-btn-secondary" state={{ topic: topicLabel(selected) }}>
                      Ask tutor
                    </Link>
                  </div>
                </>
              )}
            </aside>
          )}
        </div>
      </AdaptivePageShell>
    </>
  );
}
